import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Navbar } from '../Navbar';
import { Footer } from '../Footer';
import { MailIcon, RefreshCwIcon, CheckCircleIcon } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { auth, actionCodeSettings } from '../../firebase';
import { sendEmailVerification } from 'firebase/auth';
export const EmailVerificationPage = () => {
  const navigate = useNavigate();
  const {
    currentUser,
    refreshUser,
    userData
  } = useAuth();
  const [verificationSent, setVerificationSent] = useState(false);
  const [verificationChecking, setVerificationChecking] = useState(false);
  const [countdown, setCountdown] = useState(60);
  const [canResend, setCanResend] = useState(false);
  useEffect(() => {
    if (!currentUser) {
      navigate('/student-login');
      return;
    }
    if (currentUser.emailVerified) {
      console.log('[EmailVerificationPage] Email verified - checking onboarding status');
      if (userData?.onboardingComplete) {
        console.log('[EmailVerificationPage] Onboarding complete - redirecting to dashboard');
        navigate('/student-dashboard');
      } else if (userData?.instrument && userData?.skillLevel) {
        console.log('[EmailVerificationPage] Profile partially complete - redirecting to dashboard');
        navigate('/student-dashboard');
      } else {
        console.log('[EmailVerificationPage] Onboarding incomplete - redirecting to instrument selection');
        navigate('/instrument-selection');
      }
    }
  }, [currentUser, userData, navigate]);
  useEffect(() => {
    if (!canResend && countdown > 0) {
      const timer = setTimeout(() => {
        setCountdown(countdown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else if (countdown === 0) {
      setCanResend(true);
    }
  }, [countdown, canResend]);
  // FIXED: Always use actionCodeSettings when resending verification email
  const handleResendVerification = async () => {
    if (!currentUser || !canResend) return;
    try {
      console.log('[EmailVerificationPage] Resending verification email');
      console.log('[EmailVerificationPage] Email will link to:', actionCodeSettings.url);
      // CRITICAL FIX: Always pass actionCodeSettings
      await sendEmailVerification(currentUser, actionCodeSettings);
      console.log('[EmailVerificationPage] Verification email sent successfully');
      setVerificationSent(true);
      setCanResend(false);
      setCountdown(60);
      setTimeout(() => {
        setVerificationSent(false);
      }, 5000);
    } catch (error) {
      console.error('[EmailVerificationPage] Error sending verification email:', error);
    }
  };
  const checkVerification = async () => {
    setVerificationChecking(true);
    try {
      await refreshUser();
      await new Promise(resolve => setTimeout(resolve, 500));
      if (currentUser?.emailVerified) {
        console.log('[EmailVerificationPage] Email verified successfully!');
        if (userData?.onboardingComplete) {
          navigate('/student-dashboard');
        } else if (userData?.instrument && userData?.skillLevel) {
          navigate('/student-dashboard');
        } else {
          navigate('/instrument-selection');
        }
      } else {
        setVerificationChecking(false);
      }
    } catch (error) {
      console.error('[EmailVerificationPage] Error checking verification status:', error);
      setVerificationChecking(false);
    }
  };
  return <div className="w-full min-h-screen bg-gradient-to-r from-[#0F1626] via-[#1E3A8A] to-[#2C3E50] text-white font-sans" data-id="element-3667">
      <Navbar data-id="element-3668" />
      <main className="pt-20 pb-16" data-id="element-3669">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16" data-id="element-3670">
          <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 shadow-lg text-center" data-id="element-3671">
            <div className="bg-deep-pink/20 rounded-full p-4 w-20 h-20 mx-auto mb-6 flex items-center justify-center" data-id="element-3672">
              <MailIcon className="h-10 w-10 text-deep-pink" data-id="element-3673" />
            </div>

            <h1 className="text-3xl md:text-4xl font-serif font-bold text-white mb-4" data-id="element-3674">
              Verify Your Email
            </h1>

            <p className="text-lg text-white/80 mb-8" data-id="element-3675">
              We've sent a verification email to{' '}
              <span className="font-medium text-white" data-id="element-3676">
                {currentUser?.email}
              </span>
              . Please check your inbox and click the verification link to
              continue.
            </p>

            {verificationSent && <div className="bg-green-500/20 border border-green-500/30 text-green-300 px-4 py-3 rounded-lg mb-6 flex items-center justify-center" data-id="element-3677">
                <CheckCircleIcon className="h-5 w-5 mr-2" data-id="element-3678" />
                Verification email resent successfully!
              </div>}

            <div className="flex flex-col md:flex-row items-center justify-center gap-4 mb-8" data-id="element-3679">
              <button onClick={checkVerification} disabled={verificationChecking} className="bg-white text-navy-900 hover:bg-gray-100 font-medium py-3 px-6 rounded-lg transition-colors flex items-center justify-center min-w-[200px]" data-id="element-3680">
                {verificationChecking ? <>
                    <RefreshCwIcon className="h-5 w-5 mr-2 animate-spin" data-id="element-3681" />
                    Checking...
                  </> : <>
                    <RefreshCwIcon className="h-5 w-5 mr-2" data-id="element-3682" />
                    I've Verified My Email
                  </>}
              </button>

              <button onClick={handleResendVerification} disabled={!canResend} className="bg-transparent hover:bg-white/10 border border-white/30 text-white font-medium py-3 px-6 rounded-lg transition-colors flex items-center justify-center min-w-[200px] disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-3683">
                <MailIcon className="h-5 w-5 mr-2" data-id="element-3684" />
                {canResend ? 'Resend Email' : `Resend in ${countdown}s`}
              </button>
            </div>

            <div className="text-white/60 text-sm" data-id="element-3685">
              <p className="mb-2" data-id="element-3686">
                If you don't see the email, please check your spam folder.
              </p>
              <p data-id="element-3687">
                Need help?{' '}
                <a href="#" className="text-deep-pink hover:text-pink-400 underline" data-id="element-3688">
                  Contact Support
                </a>
              </p>
            </div>
          </div>
        </div>
      </main>
      <Footer data-id="element-3689" />
    </div>;
};